
<template>
<div>
  <chc-modal ref="addContactModal" title="New Contact">
    <div slot="body">
      <charity-add-contact :charityId="charityId"
                           v-on:refresh="refreshContactLog"
                           v-on:close="addContactHide">
      </charity-add-contact>
    </div>
  </chc-modal>
  <chc-modal ref="addNoteModal" title="New Note">
    <div slot="body">
      <charity-add-note :charityId="charityId"
                           v-on:refresh="refreshContactLog"
                           v-on:close="addNoteHide">
      </charity-add-note>
    </div>
  </chc-modal>
  <section class="wrapper charity-detail">
    <div class="charity-detail-header">
      <div class="charity-status">
        <h1> {{ data.name }} </h1>
        <chc-input id="charity-status"
                   v-model="data.status"
                   label="Status"
                   format="combo"
                   :autoCompleteValues="$store.state.constants.charity.charity_status"></chc-input>
      </div>
      <dl>
        <dt class="data-label">City, State</dt>
        <dd class="data-content"> {{ data.address_city }}, {{ data.address_state }}</dd>
      </dl>
      <dl v-if="data.agreement_document">
        <dt class="data-label">Agreement Document</dt>
        <dd class="data-content"><a @click="downloadAgreement"><span class="icon fal fa-file-pdf" aria-hidden="true"></span> Download Agreement<!-- Link to download agreement document --></a></dd>
      </dl>
    </div>
    <chc-tab-control>
      <chc-tab-pane label="Charity Information">
        <charity-information
          :data="data"
          :charityId="charityId"
          :savedContacts="savedContacts"
          @refresh="refreshUploadAgreement"
          ref="charityInformation">
          <div slot="saveButton">
            <span v-if="hasErrors" class="error-message error-block">
              There were errors processing the form. Please fix the fields above.
            </span>
            <span v-if="userEmailsChanged.length > 0 && showDeactivationConfirmation" class="error-message error-block">
              The following contact emails were changed before the associated user was deactivated. Please select which of the affected users you would like to deactivate before updating the emails:
            </span>
            <span v-if="showDeactivationConfirmation" v-for="contact in userEmailsChanged" v-bind:key="contact.email">
              <chc-checkbox v-model="contact.user.deactivate" :label="'Deactivate existing user: ' + contact.user.email"></chc-checkbox>
            </span>
            <div class="field buttons left">
              <input type="submit" class="btn" value="Save Changes" @click="saveChanges" :disabled="isLoading">
            </div>
            <transition name="fade">
              <p v-show="saved" class="success-message">Changes Saved</p>
            </transition>
          </div>
        </charity-information>
      </chc-tab-pane>
      <chc-tab-pane label="Charity Account Setup">
        <charity-account :charityId="charityId"
                         :widget="data.widget_token"
                         :hasLogo="data.logo"
                         :users='data.user_accounts'
                         :charity="data"
                         @refresh="refreshUploadAgreement"></charity-account>
      </chc-tab-pane>
      <chc-tab-pane label="Referral Sources">
        <source-summary :charityId="charityId"></source-summary>
      </chc-tab-pane>
      <chc-tab-pane label="Log">
        <charity-contact-log
          ref="contactLog"
          v-on:addContact="addContact"
          v-on:addNote="addNote"
          :charityId="charityId"></charity-contact-log>
      </chc-tab-pane>
      <chc-tab-pane label="Proceeds">
        <charity-proceeds :charityId="charityId"></charity-proceeds>
      </chc-tab-pane>
      <chc-tab-pane label="Vehicles">
        <charity-vehicles :charityId="charityId"></charity-vehicles>
      </chc-tab-pane>
  </chc-tab-control>
</section>
</div>
</template>

<script>
import CharityInformation from '@/components/Charity/CharityInformation'
import CharityAccount from '@/components/Charity/CharityAccount'
import CharityContactLog from '@/components/Charity/CharityContactLog'
import CharityAddContact from '@/components/Charity/CharityAddContact'
import CharityProceeds from '@/components/Charity/CharityProceeds'
import CharityVehicles from '@/components/Charity/CharityVehicles'
import CharityAddNote from '@/components/Charity/CharityAddNote'
import SourceSummary from '@/components/Source/SourceSummary'
import store from '@/store'
import helpers from '@/helpers'

export default {
  name: 'Charity',
  data () {
    return {
      data: {
        'add': false,
        contacts: {
          main: {},
          marketing: {},
          accounting: {},
          charity_agreement_signator: {},
        },
      },
      savedContacts: {
        main: {},
        marketing: {},
        accounting: {},
        charity_agreement_signator: {},
      },
      isLoading: false,
      saved: false,
      hasErrors: false,

      showDeactivationConfirmation: false,
      emails: [],
    }
  },
  props: ['charityId'],
  beforeRouteEnter (to, from, next) {
    let charityData = null
    Promise.all([
      store.dispatch('syncConstants'),
      store.dispatch('syncSummary'),
      store.functions.charity.getCharity(to.params.charityId, (response) => { charityData = response.data.data })
    ]).then(() => {
      window.scrollTo(0, 0)
      next(vm => vm.setData(charityData))
    })
  },
  methods: {
    setData (data) {
      this.data = data
      this.savedContacts = JSON.parse(JSON.stringify(data.contacts))
    },
    addContact () {
      this.$refs.addContactModal.show()
    },
    addContactHide () {
      this.$refs.addContactModal.hide()
    },
    addNote () {
      this.$refs.addNoteModal.show()
    },
    addNoteHide () {
      this.$refs.addNoteModal.hide()
    },
    refreshContactLog () {
      this.$refs.contactLog.refresh()
    },
    refreshUploadAgreement () {
      store.functions.charity.getCharity(this.data.id, (response) => {
        this.setData(
          Object.assign(
            response.data.data,
            // proceeds_rate_id is not accepted as an id in form submission is certain scenarios
            // so don't override.
            {proceeds_rate_id: this.data.proceeds_rate_id}
          )
        )
      })
    },
    downloadAgreement () {
      store.functions.charity.downloadAgreement(this.charityId, (error) => {
        console.log(error)
      }).then((response) => {
        helpers.openAsDownload(response.data, this.data.agreement_document_name)
      })
    },
    saveChanges () {
      if (this.userEmailsChanged.length > 0 && !this.showDeactivationConfirmation) {
        this.showDeactivationConfirmation = true
      } else {
        this.saved = false
        this.isLoading = true
        this.save()
      }
    },
    save () {
      store.functions.charity.updateCharity(
        this.charityId,
        this.data,
        (errors) => {
          this.$refs.charityInformation.errors = errors
          this.$refs.charityInformation.hasErrors = true
          this.hasErrors = true
          this.saved = false
          this.isLoading = false
        }).then((response) => {
        this.$toast.success('Changes saved.')
        this.hasErrors = false
        this.$refs.charityInformation.errors = {}
        this.$refs.charityInformation.hasErrors = false
        this.refreshUploadAgreement()
        this.emails = []
        this.showDeactivationConfirmation = false
        this.saved = true
        this.isLoading = false
        setTimeout(() => {
          this.saved = false
        }, 500)
      }).catch(() => {
        this.$toast.error('Failed to save information...')
      })
    },
  },
  computed: {
    userEmailsChanged () {
      let emails = []
      let indices = Object.keys(this.data.contacts)
      for (var i = indices.length - 1; i >= 0; i--) {
        if (this.data.contacts[indices[i]].id != null && this.data.contacts[indices[i]].user !== null &&
          this.data.contacts[indices[i]].deleted_at == null &&
          this.data.contacts[indices[i]].user.email !== this.data.contacts[indices[i]].email &&
          this.data.contacts[indices[i]].user.charity_id === parseInt(this.charityId)) {
          emails.push(this.data.contacts[indices[i]])
        }
      }
      return emails
    },
  },
  components: {
    CharityInformation,
    CharityAccount,
    CharityContactLog,
    CharityAddContact,
    CharityAddNote,
    CharityProceeds,
    CharityVehicles,
    SourceSummary,
  }
}
</script>

<style lang="scss" scoped>
/* CHARITY DETAIL */
.charity-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 2rem;
  .charity-status {
    margin-right: 2rem;
    width: 300px;
    .field {
      width: 300px;
    }
  }
  h1 {
    margin: 0 0 1rem 0;
    text-transform: none;
  }
  dl {
    margin: 0 3rem 0 0;
  }
}
.fade-enter-active, .fade-leave-active {
    opacity: 1;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    transition: opacity 3s;
    opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Charity/Charity.vue